import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { BuilderWizard, type WizardResult } from '@/components/builder/BuilderWizard';
import { DeckWizard, type DeckWizardResult } from '@/components/builder/DeckWizard';
import { DeckIcon, GlobeIcon } from '@/components/icons/MenuIcons';
import { useAuth } from '@/utils/AuthContext';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { builderApi, projectsApi, type Build } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';

/** Generation takes ~30s for a page and ~45s for a deck; the bars run a little longer. */
const GENERATION_ESTIMATE_SECONDS = 45;
const DECK_GENERATION_ESTIMATE_SECONDS = 60;

/** Status pill colors, reusing the info/success/error tints already used across the app. */
const STATUS_STYLES: Record<Build['status'], string> = {
  draft: 'bg-[var(--grey-background,#f8f8f8)] text-[color:var(--grey-text,#565858)]',
  published: 'bg-[#e8f8e8] text-[#2e7d32]',
  unpublished: 'bg-[var(--grey-background,#f8f8f8)] text-[color:var(--grey-text,#565858)]',
  suspended: 'bg-[var(--error-soft,#ffeded)] text-[color:var(--error-strong,#b3261e)]',
};

/** null — no wizard is open; otherwise the wizard for that type. */
type WizardKind = 'landing' | 'deck' | null;

export default function BuilderListPage() {
  const { t, i18n } = useTranslation('builder');
  const { isFree: freeTier, subscriptionTier } = useAuth();
  const isFree = freeTier || subscriptionTier === 'student';
  const navigate = useLocalizedNavigate();

  const [builds, setBuilds] = useState<Build[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showChooser, setShowChooser] = useState(false);
  const [wizard, setWizard] = useState<WizardKind>(null);
  const [creatingKind, setCreatingKind] = useState<Exclude<WizardKind, null> | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Build | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setIsLoading(true);
    const [buildsResult, projectsResult] = await Promise.allSettled([
      builderApi.list(),
      projectsApi.list(),
    ]);

    if (buildsResult.status === 'fulfilled') setBuilds(buildsResult.value.builds);
    if (projectsResult.status === 'fulfilled') setProjects(projectsResult.value);
    setIsLoading(false);
  }

  /**
   * Create, then generate straight away.
   *
   * Both types follow the same two calls — an empty draft is not a useful place to
   * leave someone who has just filled in a brief — and differ only in the wording of
   * the toast afterwards.
   */
  async function handleCreate(
    kind: Exclude<WizardKind, null>,
    result: WizardResult | DeckWizardResult,
  ) {
    setCreatingKind(kind);
    setWizard(null);
    const isDeck = kind === 'deck';

    try {
      const { build } = await builderApi.create({
        name: result.name,
        kind,
        projectId: result.projectId,
        brief: result.brief,
      });

      const generated = await builderApi.generate(build.id, result.brief, i18n.language as 'ru' | 'en');

      const prefix = isDeck ? 'deck.toast' : 'toast';
      toast[generated.degraded ? 'warning' : 'success'](
        generated.degraded ? t(`${prefix}.generatedDegraded`) : t(`${prefix}.generated`),
      );
      navigate(`/builder/${build.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
      setCreatingKind(null);
      void load();
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);

    try {
      await builderApi.delete(target.id);
      setBuilds((current) => current.filter((build) => build.id !== target.id));
      toast.success(t(target.kind === 'deck' ? 'deck.toast.deleted' : 'toast.deleted'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    }
  }

  if (creatingKind) {
    const isDeck = creatingKind === 'deck';
    return (
      <div className="flex flex-col gap-[24px]">
        <PageHeader title={t('page.title')} />
        <GenerationProgress
          title={t(isDeck ? 'deck.generating.title' : 'generating.title')}
          message={t(isDeck ? 'deck.generating.message' : 'generating.message')}
          estimatedDuration={isDeck ? DECK_GENERATION_ESTIMATE_SECONDS : GENERATION_ESTIMATE_SECONDS}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex flex-wrap items-start justify-between gap-[16px]">
        <PageHeader title={t('page.title')} description={t('page.description')} />
        {!isFree && builds.length > 0 && (
          <Button variant="brand" size="brand" onClick={() => setShowChooser(true)}>
            {t('list.create')}
          </Button>
        )}
      </div>

      {isFree && <SubscriptionLimitBanner description={t('limit.free')} />}

      {isLoading ? (
        <AccessibleLoader label={t('loading', { ns: 'common' })} />
      ) : builds.length === 0 ? (
        <div className="flex flex-col items-center gap-[12px] rounded-[16px] bg-[var(--grey-background,#f8f8f8)] px-[24px] py-[48px] text-center">
          <div className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-[rgba(33,151,241,0.12)]">
            <GlobeIcon size={28} className="text-[color:var(--main-brand,#2197f1)]" />
          </div>
          <h2 className="font-manrope text-[20px] font-bold text-[color:var(--black-text,#0d0d0d)]">
            {t('list.empty.title')}
          </h2>
          <p className="max-w-[520px] font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">
            {t('list.empty.text')}
          </p>
          {!isFree && (
            <Button variant="brand" size="brand" className="mt-[8px]" onClick={() => setShowChooser(true)}>
              {t('list.empty.action')}
            </Button>
          )}
        </div>
      ) : (
        <ul className="grid gap-[16px] sm:grid-cols-2 xl:grid-cols-3">
          {builds.map((build) => (
            <li
              key={build.id}
              className="flex flex-col gap-[12px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] p-[20px]"
            >
              <div className="flex items-start justify-between gap-[8px]">
                <h3 className="font-manrope text-[17px] font-bold text-[color:var(--black-text,#0d0d0d)]">
                  {build.name}
                </h3>
                <span
                  className={cn(
                    'shrink-0 rounded-[6px] px-[8px] py-[2px] font-segoe text-[12px] font-semibold',
                    STATUS_STYLES[build.status],
                  )}
                >
                  {t(`list.status.${build.status}`)}
                </span>
              </div>

              {/* Один список на два типа — значит тип надо называть. Иначе
                  «Креатор — раунд seed» и «Анна Ковалёва» стоят рядом и ничем
                  не отличаются, пока не откроешь карточку. */}
              <span className="flex w-fit items-center gap-[6px] rounded-[6px] bg-[var(--grey-background,#f8f8f8)] px-[8px] py-[2px] font-segoe text-[12px] font-semibold text-[color:var(--grey-text,#565858)]">
                {build.kind === 'deck' ? <DeckIcon size={14} /> : <GlobeIcon size={14} />}
                {t(build.kind === 'deck' ? 'type.deck' : 'type.landing')}
              </span>

              {/* Живой сайт — сразу кликабельным адресом: это то, ради чего всё
                  и делалось, и его чаще всего нужно кому-то отправить. */}
              {build.url ? (
                <a
                  href={build.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate font-segoe text-[13px] text-[color:var(--main-brand,#2197f1)] hover:underline"
                >
                  {build.url.replace(/^https:\/\//, '')}
                </a>
              ) : (
                /* Адреса ещё нет — значит под названием должно стоять то, что
                   говорит о судьбе работы. Раньше здесь был `slug ||
                   'Черновик без содержимого'`, то есть черновик с тремя
                   версиями вёрстки объявлялся пустым: slug — это адрес, а не
                   содержимое. Показываем число версий, а «без содержимого»
                   оставляем ровно для случая, когда версий и правда нет. */
                <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
                  {build.versions > 0
                    ? t('list.card.versions', { count: build.versions })
                    : t('list.card.noContent')}
                </p>
              )}

              {build.leads > 0 && (
                <p className="font-segoe text-[13px] text-[color:var(--black-text,#0d0d0d)]">
                  {t('list.card.leads', { count: build.leads })}
                </p>
              )}

              <div className="mt-auto flex gap-[8px] pt-[4px]">
                <Button variant="brand" size="brand" onClick={() => navigate(`/builder/${build.id}`)}>
                  {t('list.card.open')}
                </Button>
                <Button variant="brand-danger" size="brand" onClick={() => setDeleteTarget(build)}>
                  {t('list.card.delete')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Выбор типа отдельным шагом: мастера спрашивают разное — сайт про продукт
          и цены, презентация про команду, трекшн и запрос, — и склеивать их в
          одну форму с ветвлением значит показывать половину полей впустую. */}
      <Dialog open={showChooser} onOpenChange={setShowChooser}>
        <DialogContent className="max-h-[90vh] max-w-[560px] overflow-y-auto p-6 sm:p-8">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center font-manrope text-[24px] font-bold">
              {t('chooser.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-[12px]">
            {(['landing', 'deck'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setShowChooser(false);
                  setWizard(option);
                }}
                className="flex items-start gap-[12px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] p-[16px] text-left transition-colors hover:border-[var(--main-brand,#2197f1)] hover:bg-[rgba(33,151,241,0.04)]"
              >
                <span className="mt-[2px] flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-[rgba(33,151,241,0.12)]">
                  {option === 'deck' ? (
                    <DeckIcon size={20} className="text-[color:var(--main-brand,#2197f1)]" />
                  ) : (
                    <GlobeIcon size={20} className="text-[color:var(--main-brand,#2197f1)]" />
                  )}
                </span>
                <span className="flex flex-col gap-[2px]">
                  <span className="font-manrope text-[16px] font-bold text-[color:var(--black-text,#0d0d0d)]">
                    {t(`chooser.${option}.title`)}
                  </span>
                  <span className="font-segoe text-[14px] text-[color:var(--grey-text,#565858)]">
                    {t(`chooser.${option}.text`)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={wizard !== null} onOpenChange={(open) => !open && setWizard(null)}>
        <DialogContent className="max-h-[90vh] max-w-[560px] overflow-y-auto p-6 sm:p-8">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center font-manrope text-[24px] font-bold">
              {t(wizard === 'deck' ? 'deck.wizard.title' : 'wizard.title')}
            </DialogTitle>
          </DialogHeader>

          {wizard === 'deck' ? (
            <DeckWizard
              projects={projects}
              isSubmitting={creatingKind !== null}
              onSubmit={(result) => handleCreate('deck', result)}
              onCancel={() => setWizard(null)}
            />
          ) : (
            <BuilderWizard
              projects={projects}
              isSubmitting={creatingKind !== null}
              onSubmit={(result) => handleCreate('landing', result)}
              onCancel={() => setWizard(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title={t(deleteTarget?.kind === 'deck' ? 'deck.deleteConfirm.title' : 'list.deleteConfirm.title')}
        description={t(deleteTarget?.kind === 'deck' ? 'deck.deleteConfirm.text' : 'list.deleteConfirm.text')}
        confirmText={t('list.deleteConfirm.confirm')}
        cancelText={t('list.deleteConfirm.cancel')}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
